import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getCaseById, getCases } from "@/api/caseApi";
import { assetUrl } from "@/utils/assetUrl";
import { useSEO, buildBreadcrumbLd, SITE_ORIGIN } from "@/utils/seo";

const categoryColors: Record<string, { bg: string; text: string; border: string; accent: string }> = {
  "info-security": { bg: "bg-[#1a56db]/8", text: "text-[#1a56db]", border: "border-[#1a56db]/20", accent: "#1a56db" },
  "infrastructure": { bg: "bg-[#0891b2]/8", text: "text-[#0891b2]", border: "border-[#0891b2]/20", accent: "#0891b2" },
  "smart-integration": { bg: "bg-[#059669]/8", text: "text-[#059669]", border: "border-[#059669]/20", accent: "#059669" },
};

interface CaseMetric { value: string; label: string; }
interface CaseSolution { title: string; desc: string; icon: string; }
interface CaseTestimonial { quote: string; author: string; role: string; }
interface CaseDetail {
  id: number;
  solution: string;
  solutionName: string;
  client: string;
  title: string;
  summary: string;
  desc: string;
  tags: string[];
  metrics: CaseMetric[];
  image: string;
  background: string;
  challenges: string[];
  solutions: CaseSolution[];
  timeline: string;
  teamSize: string;
  industry: string;
  scale: string;
  testimonial?: CaseTestimonial;
}
interface CaseListItem {
  id: number;
  solution: string;
  solutionName: string;
  client: string;
  title: string;
  summary: string;
  tags: string[];
  metrics: CaseMetric[];
  image: string;
}

const caseExtended: Record<number, {
  background: string;
  challenges: string[];
  solution: { title: string; desc: string; icon: string }[];
  timeline: string;
  teamSize: string;
  industry: string;
  scale: string;
  testimonial?: { quote: string; author: string; role: string };
}> = {
  1: {
    background: "该大型股份制银行拥有数千名员工和遍布全国的分支机构，随着数字化转型加速，网络安全威胁日益复杂。原有安全体系建设于五年前，已无法应对新型攻击手段，亟需全面升级基础安全防护能力，构建符合金融行业监管要求的纵深防御体系。",
    challenges: [
      "原有防火墙设备老旧，无法识别应用层攻击，边界防护形同虚设",
      "缺乏统一的安全运营中心，安全事件发现和响应严重滞后",
      "网络准入管控缺失，终端接入混乱，内部横向移动风险极高",
      "监管合规压力大，需满足银保监会网络安全相关要求",
    ],
    solution: [
      { icon: "ri-shield-flash-line", title: "下一代防火墙部署", desc: "在互联网出口和核心区域部署NGFW，实现应用层精细化访问控制，SSL流量解密检测，有效阻断外部威胁入侵。" },
      { icon: "ri-radar-line", title: "入侵防御系统建设", desc: "部署IPS系统对全网流量进行深度检测，内置50000+威胁特征库，实时识别并自动阻断入侵行为。" },
      { icon: "ri-computer-line", title: "网络准入控制", desc: "建立802.1X网络准入控制系统，对接入终端进行身份认证和安全基线检查，不合规终端自动隔离。" },
      { icon: "ri-dashboard-3-line", title: "安全运营中心", desc: "搭建SOC安全运营中心，整合多源安全日志，实现7×24小时威胁监控，平均响应时间缩短至15分钟。" },
    ],
    timeline: "4个月",
    teamSize: "12人",
    industry: "金融银行",
    scale: "5000+ 员工",
    testimonial: { quote: "项目实施后，我们的安全防护能力得到了质的飞跃，安全事件响应时间从原来的数小时缩短到15分钟以内，监管检查也顺利通过。", author: "张总监", role: "信息科技部总监" },
  },
  2: {
    background: "该科技研发企业拥有大量核心知识产权和研发数据，是公司最重要的核心资产。随着竞争加剧，数据泄露风险日益突出，曾发生过研发人员离职带走核心代码的事件，给公司造成重大损失，迫切需要建立完善的数据安全防护体系。",
    challenges: [
      "研发数据分散存储，缺乏统一管控，数据流向不清晰",
      "内部人员泄密风险高，离职员工带走数据事件时有发生",
      "外部攻击针对研发系统，APT攻击持续渗透",
      "数据安全合规要求提升，需满足数据安全法相关规定",
    ],
    solution: [
      { icon: "ri-spam-2-line", title: "DLP数据防泄漏", desc: "部署终端、网络、邮件全渠道DLP系统，对敏感数据的传输、存储、使用进行全程监控，自动阻断违规操作。" },
      { icon: "ri-file-shield-2-line", title: "文档透明加密", desc: "引入文档加密管理平台，对研发文档实施透明加密，文件离开授权环境自动失效，彻底杜绝数据外带。" },
      { icon: "ri-file-list-3-line", title: "数据库审计", desc: "部署数据库审计系统，对所有数据库访问行为进行全量记录，异常访问实时告警，满足合规审计要求。" },
      { icon: "ri-key-2-line", title: "权限精细化管控", desc: "建立基于角色的访问控制体系，实施最小权限原则，敏感数据访问需申请审批，全程留痕可追溯。" },
    ],
    timeline: "3个月",
    teamSize: "8人",
    industry: "科技研发",
    scale: "800+ 员工",
    testimonial: { quote: "数据安全体系建立后，我们对研发数据的管控能力大幅提升，再也不用担心核心技术外泄的问题了。", author: "李总", role: "CTO" },
  },
  3: {
    background: "该制造集团拥有多个生产基地，终端设备数量庞大，分布广泛。由于历史原因，终端安全管理混乱，不同部门使用不同的安全软件，版本参差不齐，病毒感染事件频发，严重影响生产运营。",
    challenges: [
      "终端数量庞大，分布在多个生产基地，统一管理难度极高",
      "病毒感染事件频发，曾导致生产线停工，损失惨重",
      "终端安全软件版本混乱，防护能力参差不齐",
      "缺乏终端行为审计，违规操作无法追溯",
    ],
    solution: [
      { icon: "ri-computer-line", title: "统一终端安全平台", desc: "部署统一终端安全管理平台，覆盖全部3000+终端，实现集中管控、策略统一下发、状态实时监控。" },
      { icon: "ri-bug-line", title: "全面病毒防护", desc: "引入新一代终端防病毒软件，支持云查杀和本地引擎双重防护，病毒检测率达99.9%，支持离线环境使用。" },
      { icon: "ri-usb-line", title: "外设管控", desc: "实施USB等外设管控策略，禁止未授权存储设备接入，防止数据通过物理介质外泄。" },
      { icon: "ri-file-list-3-line", title: "行为审计", desc: "建立终端行为审计系统，记录文件操作、网络访问、应用使用等行为日志，违规行为实时告警。" },
    ],
    timeline: "5个月",
    teamSize: "15人",
    industry: "制造业",
    scale: "3000+ 终端",
    testimonial: { quote: "终端安全管理平台上线后，病毒感染事件几乎绝迹，生产线再也没有因为安全问题停工，效果非常显著。", author: "王经理", role: "IT运维经理" },
  },
  4: {
    background: "该省级政务单位承担重要的政务数据处理和公共服务职能，业务数据涉及大量公民个人信息和政务敏感数据。近年来，勒索病毒攻击政务机构的事件频发，该单位曾遭受过一次勒索病毒攻击尝试，虽未造成损失，但引起了高度重视。",
    challenges: [
      "政务数据价值高，是勒索攻击的重点目标",
      "现有备份系统不完善，一旦数据被加密将面临严重损失",
      "网络横向移动防护缺失，一旦突破边界将快速扩散",
      "缺乏专业的应急响应能力，事件处置效率低",
    ],
    solution: [
      { icon: "ri-shield-flash-line", title: "专业防勒索软件", desc: "部署专业防勒索软件，基于行为分析技术实时检测勒索行为，在文件被加密前自动阻断，保护关键数据。" },
      { icon: "ri-database-2-line", title: "数据备份容灾", desc: "建立三级备份体系（本地+同城+异地），采用不可变存储技术防止备份被加密，确保数据可恢复。" },
      { icon: "ri-git-branch-line", title: "网络微隔离", desc: "实施网络微隔离策略，将核心业务系统划分为独立安全域，防止勒索病毒横向扩散蔓延。" },
      { icon: "ri-first-aid-kit-line", title: "应急响应机制", desc: "建立完善的安全应急响应机制，制定勒索病毒专项应急预案，定期开展演练，确保快速有效处置。" },
    ],
    timeline: "3个月",
    teamSize: "10人",
    industry: "政务机构",
    scale: "500+ 终端",
    testimonial: { quote: "防勒索体系建立后，我们成功抵御了多次勒索攻击尝试，业务数据安全得到了有力保障，领导对此非常满意。", author: "陈科长", role: "网络安全科科长" },
  },
  5: {
    background: "该金融机构作为持牌金融机构，面临监管部门的等保三级合规要求，需要在规定时间内完成全面整改并通过测评。由于历史欠账较多，整改工作量大，时间紧迫，需要专业团队提供一站式等保建设服务。",
    challenges: [
      "等保差距较大，技术和管理两个层面均存在明显不足",
      "整改时间紧迫，需要在6个月内完成全部整改并通过测评",
      "内部IT团队经验不足，缺乏等保建设专业知识",
      "业务不能中断，整改工作需要在不影响业务的前提下进行",
    ],
    solution: [
      { icon: "ri-search-eye-line", title: "差距分析与规划", desc: "开展全面的等保差距分析，梳理技术和管理层面的整改项，制定详细的整改计划和时间表。" },
      { icon: "ri-tools-line", title: "技术整改实施", desc: "部署数据库审计、日志审计、漏洞扫描、堡垒机、WAF等安全设备，完成技术层面全部整改项。" },
      { icon: "ri-file-list-3-line", title: "管理制度建设", desc: "协助建立完善的安全管理制度体系，包括安全策略、操作规程、应急预案等，满足管理层面要求。" },
      { icon: "ri-graduation-cap-line", title: "培训与测评辅导", desc: "组织全员安全意识培训，提供测评辅导服务，协助准备测评材料，确保顺利通过等保三级测评。" },
    ],
    timeline: "6个月",
    teamSize: "10人",
    industry: "金融机构",
    scale: "200+ 员工",
    testimonial: { quote: "在专业团队的帮助下，我们一次性通过了等保三级测评，整个过程非常顺利，服务质量非常高。", author: "刘主任", role: "信息技术部主任" },
  },
  6: {
    background: "该大型企业集团拥有总部及50余个分支机构，原有网络架构建设于十年前，核心交换设备老旧，带宽严重不足，网络故障频发，严重制约了业务发展。随着集团数字化转型加速，亟需重新规划建设现代化网络基础架构。",
    challenges: [
      "核心网络设备老旧，性能瓶颈严重，无法支撑业务增长",
      "分支机构互联采用传统专线，成本高且灵活性差",
      "无线网络覆盖不完整，移动办公体验差",
      "网络管理分散，缺乏统一的可视化管理平台",
    ],
    solution: [
      { icon: "ri-router-line", title: "核心网络重建", desc: "采用华为高端核心交换机构建双活核心网络，实现毫秒级故障切换，网络可用性达99.99%。" },
      { icon: "ri-global-line", title: "SD-WAN部署", desc: "引入SD-WAN技术替代传统专线，实现多链路智能调度，带宽成本降低40%，网络质量显著提升。" },
      { icon: "ri-wifi-line", title: "无线网络全覆盖", desc: "部署Wi-Fi 6无线网络，实现总部和各分支机构全覆盖，支持高密度接入，移动办公体验大幅提升。" },
      { icon: "ri-dashboard-line", title: "统一网络管理", desc: "建立统一网络管理平台，实现全网设备可视化管理，故障自动告警，运维效率提升60%。" },
    ],
    timeline: "6个月",
    teamSize: "20人",
    industry: "企业集团",
    scale: "50+ 站点",
    testimonial: { quote: "网络改造完成后，员工的办公体验得到了极大改善，网络故障投诉几乎消失，业务系统响应速度也明显加快。", author: "赵总", role: "集团IT总监" },
  },
  7: {
    background: "该互联网企业员工分布在全国多个城市，传统PC管理成本高，数据安全风险大，员工异地办公体验差。随着公司规模扩张，IT运维压力持续增大，亟需通过桌面云技术实现终端管理的集中化和标准化。",
    challenges: [
      "员工分布广泛，传统PC管理成本高，运维人员不足",
      "终端数据安全风险大，员工离职带走数据风险难以管控",
      "异地办公体验差，VPN访问内网速度慢",
      "PC采购和维护成本高，硬件生命周期管理复杂",
    ],
    solution: [
      { icon: "ri-cloud-line", title: "桌面云平台建设", desc: "采用华为FusionAccess桌面云平台，在数据中心集中部署虚拟桌面，实现办公终端统一管理和交付。" },
      { icon: "ri-computer-line", title: "瘦客户端部署", desc: "部署瘦客户端替代传统PC，硬件成本降低60%，维护简单，使用寿命长，大幅降低终端TCO。" },
      { icon: "ri-lock-2-line", title: "数据集中存储", desc: "所有数据集中存储在数据中心，终端本地无数据，彻底消除终端数据泄露风险，满足数据安全要求。" },
      { icon: "ri-settings-3-line", title: "统一运维管理", desc: "建立统一运维管理平台，实现桌面云资源的统一分配、监控和管理，运维效率提升80%。" },
    ],
    timeline: "4个月",
    teamSize: "12人",
    industry: "互联网",
    scale: "500+ 用户",
    testimonial: { quote: "桌面云上线后，IT运维工作量大幅减少，员工无论在哪里都能获得一致的办公体验，效果超出预期。", author: "孙总监", role: "IT基础设施总监" },
  },
  8: {
    background: "该金融科技公司业务快速增长，传统IT架构扩展困难，新业务上线周期长，资源利用率低，运维复杂度高。为支撑业务快速发展，需要引入超融合架构，实现IT基础设施的现代化升级。",
    challenges: [
      "传统三层架构扩展困难，新业务上线需要数周时间",
      "服务器、存储、网络分别管理，运维复杂度高",
      "资源利用率低，大量计算和存储资源闲置浪费",
      "业务快速增长，IT基础设施需要快速弹性扩展",
    ],
    solution: [
      { icon: "ri-stack-line", title: "超融合一体机部署", desc: "采用华为FusionCube超融合一体机，将计算、存储、网络融合为一体，即插即用，快速扩展。" },
      { icon: "ri-cpu-line", title: "服务器虚拟化", desc: "基于KVM虚拟化技术，将物理服务器资源池化，实现资源弹性分配，资源利用率提升至75%。" },
      { icon: "ri-hard-drive-2-line", title: "分布式存储", desc: "采用分布式存储架构，实现存储资源的线性扩展，性能随节点增加线性提升，无单点故障。" },
      { icon: "ri-dashboard-line", title: "统一管理平台", desc: "建立统一管理平台，实现计算、存储、网络资源的可视化管理，自动化运维，大幅降低运维复杂度。" },
    ],
    timeline: "3个月",
    teamSize: "8人",
    industry: "金融科技",
    scale: "200+ 虚拟机",
    testimonial: { quote: "超融合架构上线后，新业务上线时间从原来的数周缩短到几小时，IT基础设施终于能够跟上业务发展的步伐了。", author: "周总", role: "技术副总裁" },
  },
  9: {
    background: "该中型企业IT团队规模有限，仅有3名IT人员，无法支撑日益复杂的IT基础设施运维需求。随着业务系统增多，运维压力持续增大，系统故障频发，严重影响业务运营，亟需通过托管云服务解决运维难题。",
    challenges: [
      "IT团队规模小，无法支撑复杂IT系统的专业运维",
      "系统故障频发，响应不及时，业务损失大",
      "缺乏专业的安全运维能力，安全风险高",
      "IT基础设施投入大，但利用率低，成本效益差",
    ],
    solution: [
      { icon: "ri-cloud-line", title: "业务系统云迁移", desc: "将核心业务系统迁移至专业托管云平台，享受高可用、高性能的云基础设施，告别自建机房烦恼。" },
      { icon: "ri-customer-service-2-line", title: "7×24专业运维", desc: "提供7×24小时专业运维服务，建立完善的监控告警体系，故障平均响应时间小于15分钟。" },
      { icon: "ri-shield-check-line", title: "安全运维保障", desc: "提供专业的安全运维服务，定期安全巡检、漏洞修复、安全加固，确保系统安全合规运行。" },
      { icon: "ri-bar-chart-2-line", title: "性能优化服务", desc: "定期开展系统性能分析和优化，提供容量规划建议，确保系统始终保持最佳运行状态。" },
    ],
    timeline: "2个月",
    teamSize: "6人",
    industry: "中型企业",
    scale: "50+ 服务器",
    testimonial: { quote: "托管云服务让我们的IT运维压力大幅减轻，系统稳定性显著提升，IT成本也降低了，非常值得推荐。", author: "吴总", role: "总经理" },
  },
  10: {
    background: "该集团企业IT基础设施使用年限超过8年，服务器、存储、网络设备全面老化，性能瓶颈严重，故障频发，严重影响业务运营。面对数字化转型的迫切需求，决定对IT基础设施进行全面升级改造。",
    challenges: [
      "IT设备全面老化，性能严重不足，无法支撑业务增长",
      "故障频发，维修成本高，备件难以获取",
      "能耗高，散热差，机房运营成本居高不下",
      "升级改造需要在不中断业务的前提下进行，难度极大",
    ],
    solution: [
      { icon: "ri-server-line", title: "服务器全面升级", desc: "更换新一代高性能服务器，计算性能提升5倍，内存容量翻倍，支持最新虚拟化技术，为业务发展提供充足算力。" },
      { icon: "ri-hard-drive-2-line", title: "全闪存储部署", desc: "引入全闪存储阵列，存储性能提升10倍，延迟降低至微秒级，彻底解决存储性能瓶颈。" },
      { icon: "ri-router-line", title: "网络设备升级", desc: "升级核心和接入层网络设备，带宽提升至万兆，部署SDN网络管理，实现网络可视化管理。" },
      { icon: "ri-shield-flash-line", title: "安全设备更新", desc: "部署新一代安全设备，包括NGFW、IPS、WAF等，全面提升安全防护能力，满足等保合规要求。" },
    ],
    timeline: "8个月",
    teamSize: "25人",
    industry: "集团企业",
    scale: "全栈升级",
    testimonial: { quote: "IT基础设施升级后，系统性能大幅提升，故障率几乎降为零，员工工作效率明显提高，这次升级非常成功。", author: "郑总监", role: "集团CIO" },
  },
  11: {
    background: "该产业园区入驻企业众多，人员车辆流动频繁，传统门禁管理依赖人工核验，效率低下，安全隐患大。随着园区规模扩大，亟需引入智能化门禁道闸系统，实现人员车辆的精细化管控。",
    challenges: [
      "人员车辆流动量大，人工核验效率低，高峰期拥堵严重",
      "外来访客管理混乱，无法有效追踪人员动向",
      "车辆停放管理混乱，停车位利用率低",
      "安全事件发生后，缺乏有效的追溯手段",
    ],
    solution: [
      { icon: "ri-scan-2-line", title: "人脸识别门禁", desc: "安装人脸识别门禁设备，毫秒级识别，支持活体检测，实现人员快速通行与精准管控，通行效率提升300%。" },
      { icon: "ri-car-line", title: "车牌识别道闸", desc: "部署车牌识别道闸系统，支持固定车辆自动放行，临时车辆计费管理，停车效率大幅提升。" },
      { icon: "ri-group-line", title: "访客管理系统", desc: "建立访客管理系统，访客提前预约、扫码核验、全程轨迹记录，规范外来人员管理。" },
      { icon: "ri-dashboard-line", title: "统一管理平台", desc: "搭建统一管理平台，实现人员车辆数据统一管理，实时查看通行记录，异常情况自动告警。" },
    ],
    timeline: "3个月",
    teamSize: "10人",
    industry: "产业园区",
    scale: "5000+ 日均通行",
    testimonial: { quote: "智能门禁系统上线后，园区通行效率大幅提升，安全管理水平也上了一个台阶，入驻企业反馈非常好。", author: "林主任", role: "园区管理主任" },
  },
  12: {
    background: "该新建园区从零开始建设，需要同步规划和建设完整的弱电智能化系统。园区规划面积大，系统种类多，建设周期紧，需要一家有实力的集成商承接全系统建设，确保各系统协调运行。",
    challenges: [
      "系统种类多，涉及综合布线、安防、门禁、停车、楼控等多个子系统",
      "建设周期紧，需要与土建工程同步推进",
      "各系统需要互联互通，实现统一管理",
      "建设完成后需要提供长期运维保障",
    ],
    solution: [
      { icon: "ri-router-line", title: "综合布线系统", desc: "完成园区综合布线系统建设，采用超六类铜缆和万兆光纤，为各系统提供高质量的传输基础。" },
      { icon: "ri-camera-line", title: "安防监控系统", desc: "部署200+高清摄像头，实现园区全覆盖监控，引入AI智能分析，支持人脸识别和行为分析。" },
      { icon: "ri-door-lock-line", title: "门禁停车系统", desc: "建设智能门禁和停车管理系统，实现人员车辆精细化管控，提升园区安全管理水平。" },
      { icon: "ri-temp-hot-line", title: "楼宇自控系统", desc: "部署楼宇自控系统，实现暖通空调、照明、能耗的智能管控，能耗降低30%，运营成本大幅下降。" },
    ],
    timeline: "12个月",
    teamSize: "30人",
    industry: "产业园区",
    scale: "20万㎡",
    testimonial: { quote: "从综合布线到楼宇自控，所有弱电系统都由一家公司承建，协调配合非常顺畅，最终交付质量超出预期。", author: "陈总", role: "园区开发总经理" },
  },
  13: {
    background: "该上市公司在全国设有20余个分支机构，高管和业务团队出差频繁，出差成本高，远程协作效率低。随着疫情后混合办公模式的普及，亟需建立高质量的视频会议系统，支持高效的远程协作。",
    challenges: [
      "分支机构众多，出差成本高，每年差旅费用超过500万元",
      "现有视频会议系统画质差，经常卡顿，严重影响会议效果",
      "会议室预约管理混乱，资源利用率低",
      "移动端参会体验差，无法随时随地参与会议",
    ],
    solution: [
      { icon: "ri-video-line", title: "高清视频会议终端", desc: "在总部和各分支机构部署华为高清视频会议终端，支持1080P高清画质，音视频效果媲美面对面会议。" },
      { icon: "ri-calendar-line", title: "会议管理平台", desc: "建立视频会议管理平台，实现会议室统一预约管理，会议自动提醒，资源利用率提升50%。" },
      { icon: "ri-smartphone-line", title: "移动端接入", desc: "支持手机、平板等移动设备接入，员工随时随地参与会议，移动办公体验大幅提升。" },
      { icon: "ri-git-merge-line", title: "OA系统集成", desc: "与OA系统深度集成，会议邀请自动同步日历，会议纪要自动归档，提升会议管理效率。" },
    ],
    timeline: "2个月",
    teamSize: "8人",
    industry: "上市公司",
    scale: "20+ 会议室",
    testimonial: { quote: "视频会议系统上线后，出差次数明显减少，每年节省差旅费用超过200万元，投资回报非常显著。", author: "黄总", role: "行政总监" },
  },
  14: {
    background: "该大型企业厂区面积广阔，原有安防监控系统建设于8年前，摄像头分辨率低，存在大量监控盲区，无法满足现代安防管理需求。随着企业对安全管理要求的提升，决定对安防监控系统进行全面升级。",
    challenges: [
      "原有摄像头分辨率低，画质模糊，无法有效识别人员和车辆",
      "监控盲区多，厂区边角地带缺乏有效覆盖",
      "缺乏智能分析能力，安全事件发现依赖人工巡视",
      "录像存储时间短，事后追溯困难",
    ],
    solution: [
      { icon: "ri-camera-line", title: "4K高清摄像头", desc: "安装500+台4K高清摄像头，覆盖厂区全部区域，消除监控盲区，画质清晰，夜间效果优异。" },
      { icon: "ri-brain-line", title: "AI智能分析", desc: "引入AI智能分析功能，实现人员行为异常检测、周界入侵报警、人员密度分析，安全事件发现率提升200%。" },
      { icon: "ri-hard-drive-2-line", title: "视频存储系统", desc: "部署大容量视频存储系统，满足500+路摄像头90天录像存储需求，支持快速检索和回放。" },
      { icon: "ri-dashboard-3-line", title: "集中监控中心", desc: "建立集中监控中心，配备大屏显示系统，实现全厂区统一监控管理，支持多画面实时查看。" },
    ],
    timeline: "4个月",
    teamSize: "15人",
    industry: "大型企业",
    scale: "500+ 摄像头",
    testimonial: { quote: "新的安防监控系统让我们对厂区安全状况了如指掌，AI智能分析功能更是大大减轻了安保人员的工作压力。", author: "马总监", role: "安全生产总监" },
  },
};

export default function CaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const caseId = Number(id);

  const [caseData, setCaseData] = useState<CaseDetail | null>(null);
  const [relatedCases, setRelatedCases] = useState<CaseListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showBackBtn, setShowBackBtn] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const heroRef = useRef<HTMLElement>(null);

  useSEO(
    caseData
      ? {
          title: `${caseData.title} | 成功案例`,
          description:
            (caseData.summary || caseData.desc || "").slice(0, 160) ||
            `欧格登${caseData.solutionName || ""}成功案例：${caseData.title}。`,
          keywords: [
            caseData.client,
            caseData.title,
            caseData.solutionName,
            (caseData as any).industryName || caseData.industry,
            ...(caseData.tags || []),
          ].filter(Boolean) as string[],
          path: `/cases/${id}`,
          image: caseData.image
            ? caseData.image.startsWith("http")
              ? caseData.image
              : `${SITE_ORIGIN}${caseData.image}`
            : undefined,
          ogType: "article",
          jsonLd: [
            buildBreadcrumbLd([
              { name: "首页", path: "/" },
              { name: "成功案例", path: "/cases" },
              { name: caseData.title, path: `/cases/${id}` },
            ]),
            {
              "@context": "https://schema.org",
              "@type": "Article",
              headline: caseData.title,
              description: caseData.summary || caseData.desc || "",
              image: caseData.image || undefined,
              author: { "@type": "Organization", name: "欧格登" },
              publisher: {
                "@type": "Organization",
                name: "欧格登",
                logo: { "@type": "ImageObject", url: `${SITE_ORIGIN}/favicon.png` },
              },
            },
          ],
        }
      : {
          title: "成功案例",
          description: "欧格登成功案例详情",
          path: `/cases/${id ?? ""}`,
        },
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    async function fetchDetail() {
      setLoading(true);
      setError(null);
      try {
        const res: any = await getCaseById(caseId);
        const detail = res.data ?? res;
        setCaseData(detail);
        // Fetch related cases from same solution category
        const casesRes: any = await getCases({ solution: detail.solution });
        const rows: CaseListItem[] = casesRes.rows ?? casesRes.data ?? (Array.isArray(casesRes) ? casesRes : []);
        setRelatedCases(rows.filter((c) => c.id !== caseId).slice(0, 3));
      } catch (e: any) {
        console.error("加载案例详情失败", e);
        setError("加载案例详情失败，请稍后重试");
      } finally {
        setLoading(false);
      }
    }
    fetchDetail();
  }, [caseId]);

  useEffect(() => {
    const handleScroll = () => {
      const heroHeight = heroRef.current?.offsetHeight ?? 400;
      setShowBackBtn(window.scrollY > heroHeight - 72);
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  if (loading) {
    return (
      <main className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="text-center text-gray-400">加载中...</div>
      </main>
    );
  }

  if (error || !caseData) {
    return (
      <main className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">{error || "案例不存在"}</p>
          <button onClick={() => navigate("/cases")} className="px-6 py-2 bg-[#0d1f3c] text-white rounded-lg text-sm cursor-pointer whitespace-nowrap">
            返回案例列表
          </button>
        </div>
      </main>
    );
  }

  const style = categoryColors[caseData.solution] || { bg: "bg-gray-50", text: "text-gray-600", border: "border-gray-100", accent: "#374151" };
  const accentColor = style.accent;

  // 优先从API数据取值，fallback到硬编码mock
  const mock = caseExtended[caseId] || {} as any;
  const extended = {
    background: caseData.background || mock.background || "",
    challenges: (caseData as any).challenges?.map?.((c: any) => c.text ?? c) ?? mock.challenges ?? [],
    solution: (caseData as any).solutions?.map?.((s: any) => ({ icon: s.icon || "", title: s.title || "", desc: s.desc ?? s.description ?? "" })) ?? mock.solution ?? [],
    timeline: (caseData as any).projectDuration || mock.timeline || "",
    teamSize: (caseData as any).teamSize || mock.teamSize || "",
    industry: (caseData as any).industryName || (caseData as any).industry || mock.industry || "",
    scale: (caseData as any).projectScale || (caseData as any).clientScale || mock.scale || "",
    testimonial: (caseData as any).testimonial
      ? { quote: (caseData as any).testimonial, author: (caseData as any).testimonialAuthor || "", role: (caseData as any).testimonialPosition || "" }
      : mock.testimonial || null,
  };

  return (
    <main className="min-h-screen bg-[#f8fafc] overflow-x-hidden">

      {/* Fixed Back Button - appears after scrolling past hero */}
      <button
        onClick={() => navigate("/cases")}
        className={`fixed left-6 z-50 w-11 h-11 flex items-center justify-center rounded-full text-white transition-all duration-300 cursor-pointer ${
          showBackBtn ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none"
        }`}
        style={{ top: "100px", backgroundColor: accentColor, boxShadow: `0 4px 16px ${accentColor}55` }}
        aria-label="返回案例列表"
      >
        <i className="ri-arrow-left-line text-xl"></i>
      </button>

      {/* Fixed Back to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed right-5 z-50 w-12 h-12 flex items-center justify-center rounded-full text-white transition-all duration-300 cursor-pointer ${
          showBackToTop ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
        style={{ backgroundColor: accentColor, bottom: "96px", boxShadow: `0 4px 16px ${accentColor}55` }}
        aria-label="返回顶部"
      >
        <i className="ri-arrow-up-line text-lg"></i>
      </button>

      {/* Hero Section */}
      <section ref={heroRef} className="bg-[#0d1f3c] pt-28 pb-0 relative overflow-hidden">
        {/* Background image overlay */}
        <div className="absolute inset-0 opacity-20">
          <img src={assetUrl(caseData.image)} alt="" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d1f3c]/80 to-[#0d1f3c]"></div>
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-gray-400 mb-8">
            <Link to="/" className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">首页</Link>
            <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </div>
            <Link to="/cases" className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">成功案例</Link>
            <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </div>
            <span className="text-gray-300 truncate max-w-[240px]">{caseData.title}</span>
          </nav>

          <div className="flex flex-col lg:flex-row gap-10 pb-10">
            {/* Left: Info */}
            <div className="flex-1 min-w-0">
              {/* Badges */}
              <div className="flex flex-wrap items-center gap-2 mb-5">
                <span
                  className="px-3 py-1 rounded-full text-xs font-semibold"
                  style={{ backgroundColor: `${accentColor}25`, color: "#f0c0c0" }}
                >
                  {caseData.solutionName}
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-gray-300">
                  {extended.industry}
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-gray-300">
                  {extended.scale}
                </span>
              </div>

              <h1 className="text-3xl lg:text-4xl font-bold text-white mb-3 leading-tight">{caseData.title}</h1>
              <p className="text-gray-400 text-sm mb-5">{caseData.client}</p>
              <p className="text-gray-300 text-sm leading-relaxed mb-7 max-w-2xl">{caseData.desc}</p>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                {caseData.tags.map((tag) => (
                  <div
                    key={tag}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                    style={{ backgroundColor: `${accentColor}20`, color: "#e0d0d0" }}
                  >
                    <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-xs" style={{ color: accentColor }}></i>
                    </div>
                    {tag}
                  </div>
                ))}
              </div>

              {/* Key metrics row */}
              <div className="flex flex-wrap gap-6 mb-8">
                <div>
                  <div className="text-xl font-bold text-white">{extended.timeline}</div>
                  <div className="text-xs text-gray-400 mt-0.5">项目周期</div>
                </div>
                <div>
                  <div className="text-xl font-bold text-white">{extended.teamSize}</div>
                  <div className="text-xs text-gray-400 mt-0.5">项目团队</div>
                </div>
              </div>

              {/* CTA buttons */}
              <div className="flex flex-wrap gap-3">
                <a
                  href="/#contact-form"
                  className="px-7 py-3 rounded-full text-sm font-semibold text-white transition-all duration-200 whitespace-nowrap cursor-pointer"
                  style={{ backgroundColor: accentColor }}
                >
                  获取类似方案
                </a>
                <button
                  onClick={() => navigate("/cases")}
                  className="px-7 py-3 rounded-full text-sm font-semibold text-white border border-white/20 hover:bg-white/10 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  返回列表
                </button>
              </div>
            </div>

            {/* Right: Case image */}
            <div className="w-full lg:w-[420px] flex-shrink-0">
              <div className="rounded-2xl overflow-hidden bg-white/5 border border-white/10 h-64 lg:h-72">
                <img
                  src={assetUrl(caseData.image)}
                  alt={caseData.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Testimonial preview in hero */}
              {extended.testimonial && (
                <div className="mt-4 p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: accentColor }}>
                      {extended.testimonial.author[0]}
                    </div>
                    <div>
                      <p className="text-xs text-gray-300 leading-relaxed italic line-clamp-2">"{extended.testimonial.quote}"</p>
                      <p className="text-xs text-gray-500 mt-1.5">{extended.testimonial.author} · {extended.testimonial.role}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 lg:py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-10">
            {/* Left: Main Content */}
            <div className="flex-1 min-w-0">
              {/* Project Background */}
              <div className="mb-10">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-1 h-6 rounded-full" style={{ backgroundColor: accentColor }}></div>
                  <h2 className="text-lg font-bold text-[#0d1f3c]">项目背景</h2>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{extended.background}</p>
              </div>

              {/* Challenges */}
              <div className="mb-10">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-1 h-6 rounded-full" style={{ backgroundColor: accentColor }}></div>
                  <h2 className="text-lg font-bold text-[#0d1f3c]">面临挑战</h2>
                </div>
                <div className="space-y-3">
                  {extended.challenges.map((challenge, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-gray-100">
                      <div className="w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5" style={{ backgroundColor: `${accentColor}15` }}>
                        <span className="text-xs font-bold" style={{ color: accentColor }}>{idx + 1}</span>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">{challenge}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Solution */}
              <div className="mb-10">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-1 h-6 rounded-full" style={{ backgroundColor: accentColor }}></div>
                  <h2 className="text-lg font-bold text-[#0d1f3c]">解决方案</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {extended.solution.map((item, idx) => (
                    <div key={idx} className="p-5 bg-white rounded-xl border border-gray-100 hover:border-gray-200 transition-colors">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: `${accentColor}12` }}>
                          <i className={`${item.icon} text-base`} style={{ color: accentColor }}></i>
                        </div>
                        <h3 className="text-sm font-bold text-[#0d1f3c]">{item.title}</h3>
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Project Results */}
              <div className="mb-10">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-1 h-6 rounded-full" style={{ backgroundColor: accentColor }}></div>
                  <h2 className="text-lg font-bold text-[#0d1f3c]">项目成果</h2>
                </div>
                <div className="space-y-3">
                  {caseData.metrics.map((m, idx) => (
                    <div key={m.label || idx} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-gray-100">
                      <div className="w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5" style={{ backgroundColor: `${accentColor}15` }}>
                        <span className="text-xs font-bold" style={{ color: accentColor }}>{idx + 1}</span>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">{m.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Testimonial */}
              {extended.testimonial && (
                <div className="mb-10">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-1 h-6 rounded-full" style={{ backgroundColor: accentColor }}></div>
                    <h2 className="text-lg font-bold text-[#0d1f3c]">客户评价</h2>
                  </div>
                  <div className="p-6 bg-white rounded-xl border border-gray-100 relative">
                    <div className="text-5xl text-gray-100 font-serif absolute top-4 left-5 leading-none select-none">"</div>
                    <p className="text-gray-600 text-sm leading-relaxed italic relative z-10 mb-4 pl-4">
                      {extended.testimonial.quote}
                    </p>
                    <div className="flex items-center gap-3 pl-4">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: accentColor }}>
                        {extended.testimonial.author[0]}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-[#0d1f3c]">{extended.testimonial.author}</div>
                        <div className="text-xs text-gray-400">{caseData.client} · {extended.testimonial.role}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right: Sidebar */}
            <div className="w-full lg:w-72 flex-shrink-0">
              <div className="bg-white rounded-xl border border-gray-100 p-6 mb-5 sticky top-16">
                <h3 className="text-sm font-bold text-[#0d1f3c] mb-4">项目信息</h3>
                <div className="space-y-3">
                  {[
                    { icon: "ri-building-line", label: "客户名称", value: caseData.client },
                    { icon: "ri-briefcase-line", label: "所属行业", value: extended.industry },
                    { icon: "ri-shield-line", label: "解决方案", value: caseData.solutionName },
                    { icon: "ri-time-line", label: "项目周期", value: extended.timeline },
                    { icon: "ri-team-line", label: "项目团队", value: extended.teamSize },
                    { icon: "ri-expand-diagonal-line", label: "项目规模", value: extended.scale },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-3">
                      <div className="w-7 h-7 flex items-center justify-center rounded-md flex-shrink-0 mt-0.5" style={{ backgroundColor: `${accentColor}10` }}>
                        <i className={`${item.icon} text-xs`} style={{ color: accentColor }}></i>
                      </div>
                      <div>
                        <div className="text-xs text-gray-400">{item.label}</div>
                        <div className="text-sm font-medium text-[#0d1f3c]">{item.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 pt-5 border-t border-gray-100">
                  <a
                    href="/#contact-form"
                    className="block w-full py-2.5 text-center text-sm font-semibold text-white rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                    style={{ backgroundColor: accentColor }}
                  >
                    获取类似解决方案
                  </a>
                </div>
              </div>

              {/* Tags */}
              <div className="bg-white rounded-xl border border-gray-100 p-5 mb-5">
                <h3 className="text-sm font-bold text-[#0d1f3c] mb-3">相关标签</h3>
                <div className="flex flex-wrap gap-2">
                  {caseData.tags.map((tag) => (
                    <span key={tag} className={`px-3 py-1 text-xs rounded-full ${style.bg} ${style.text} border ${style.border}`}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Related solution link */}
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <h3 className="text-sm font-bold text-[#0d1f3c] mb-3">关联解决方案</h3>
                <Link
                  to="/solutions"
                  className="flex items-center gap-3 p-3 rounded-xl transition-all duration-200 hover:opacity-80 cursor-pointer"
                  style={{ backgroundColor: `${accentColor}08` }}
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: `${accentColor}20` }}>
                    <i className="ri-arrow-right-up-line text-sm" style={{ color: accentColor }}></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[#0d1f3c]">{caseData.solutionName}</p>
                    <p className="text-xs text-gray-400">查看完整解决方案</p>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Cases */}
      {relatedCases.length > 0 && (
        <section className="py-12 bg-white border-t border-gray-100">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-5 rounded-full" style={{ backgroundColor: accentColor }}></div>
              <h2 className="text-base font-bold text-[#0d1f3c]">相关案例推荐</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {relatedCases.map((c) => {
                const s = categoryColors[c.solution] || { bg: "bg-gray-50", text: "text-gray-600", border: "border-gray-100", accent: "#374151" };
                return (
                  <Link
                    key={c.id}
                    to={`/cases/${c.id}`}
                    className="group bg-[#f8fafc] rounded-xl border border-gray-100 overflow-hidden hover:border-gray-200 hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-full h-40 overflow-hidden">
                      <img src={assetUrl(c.image)} alt={c.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${s.bg} ${s.text} border ${s.border}`}>{c.solutionName}</span>
                      </div>
                      <h3 className="text-sm font-bold text-[#0d1f3c] line-clamp-2 leading-snug mb-2">{c.title}</h3>
                      <div className="text-xs font-semibold flex items-center gap-1" style={{ color: s.accent }}>
                        查看详情
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-arrow-right-line text-xs"></i>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
